import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Video, Upload, Code } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Block } from "@/types/proposals";
import { useAuth } from "@/hooks/useAuth";
import { useFileUpload } from "@/hooks/useFileUpload";

interface VideoEmbedBlockProps {
  block: Block;
  onUpdate: (updates: Partial<Block>) => void;
  isEditing?: boolean;
  proposalId?: string;
}

export function VideoEmbedBlock({ block, onUpdate, isEditing, proposalId }: VideoEmbedBlockProps) {
  const [showModal, setShowModal] = useState(false);
  const [embedCode, setEmbedCode] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  
  const { orgId } = useAuth();
  const { uploadFile, uploading } = useFileUpload(orgId || '');

  const videoData = block.content?.video;

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !proposalId || !orgId) return;

    if (!file.type.startsWith('video/')) {
      toast.error("Please upload a video file");
      return;
    }

    try {
      const result = await uploadFile(file, {
        bucket: 'proposal-videos',
        folder: proposalId,
      });

      if (!result) return;

      // Save to database
      await supabase
        .from('proposal_videos')
        .insert({
          proposal_document_id: proposalId,
          storage_path: result.path,
          url: result.url,
          video_type: 'uploaded'
        });

      onUpdate({
        content: {
          ...block.content,
          video: {
            url: result.url,
            type: 'uploaded'
          }
        }
      });

      toast.success("Video uploaded successfully");
      setShowModal(false);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error("Failed to upload video");
    }
  };

  const handleEmbedCode = () => {
    if (!embedCode.trim()) {
      toast.error("Please enter embed code");
      return;
    }

    // Allowed video domains for security
    const allowedDomains = [
      'youtube.com',
      'youtu.be',
      'vimeo.com',
      'player.vimeo.com',
      'wistia.com',
      'fast.wistia.net'
    ];

    // Detect video type and validate domain
    let type = 'custom';
    let finalUrl = embedCode;
    let isValidDomain = false;

    if (embedCode.includes('youtube.com') || embedCode.includes('youtu.be')) {
      type = 'youtube';
      isValidDomain = true;
      // Extract video ID and create embed URL
      const match = embedCode.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
      if (match) {
        finalUrl = `https://www.youtube.com/embed/${match[1]}`;
      }
    } else if (embedCode.includes('vimeo.com')) {
      type = 'vimeo';
      isValidDomain = true;
      const match = embedCode.match(/vimeo\.com\/(\d+)/);
      if (match) {
        finalUrl = `https://player.vimeo.com/video/${match[1]}`;
      }
    } else {
      // For custom embeds, validate the src URL is from allowed domains
      const srcMatch = embedCode.match(/src=["']([^"']+)["']/);
      if (srcMatch) {
        const url = srcMatch[1];
        isValidDomain = allowedDomains.some(domain => url.includes(domain));
        if (isValidDomain) {
          finalUrl = url;
        }
      }
    }

    if (!isValidDomain) {
      toast.error("Only YouTube, Vimeo, and Wistia videos are allowed for security reasons");
      return;
    }

    onUpdate({
      content: {
        ...block.content,
        video: {
          url: finalUrl,
          type,
          embedCode
        }
      }
    });

    toast.success("Video embedded successfully");
    setShowModal(false);
    setEmbedCode("");
  };

  const renderVideo = () => {
    if (!videoData) return null;

    const autoplay = block.settings?.autoplay ? 'autoplay' : '';
    const loop = block.settings?.loop ? 'loop' : '';
    const muted = block.settings?.muted ? 'muted' : '';
    const width = block.settings?.videoWidth || '100%';
    const height = block.settings?.videoHeight || '400px';

    if (videoData.type === 'uploaded') {
      return (
        <video
          src={videoData.url}
          controls
          {...{ [autoplay]: true, [loop]: true, [muted]: true }}
          style={{ width, height }}
          className="rounded"
        />
      );
    }

    // Render safe iframe (never use dangerouslySetInnerHTML for embeds)
    return (
      <iframe 
        src={videoData.url}
        width={width}
        height={height}
        frameBorder="0"
        allowFullScreen
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        title="Video embed"
      />
    );
  };

  return (
    <>
      <Card className="p-6">
        {!videoData ? (
          <div className="text-center py-12 border-2 border-dashed rounded">
            <Video className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <p className="text-sm text-muted-foreground mb-4">
              {isEditing ? "Click to add a video" : "Video will appear here"}
            </p>
            {isEditing && (
              <Button onClick={() => setShowModal(true)} variant="outline">
                <Video className="w-4 h-4 mr-2" />
                Add Video
              </Button>
            )}
          </div>
        ) : (
          <div>
            {renderVideo()}
            {isEditing && (
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={() => setShowModal(true)}
              >
                Change Video
              </Button>
            )}
          </div>
        )}
      </Card>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Video</DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="upload">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="upload">
                <Upload className="w-4 h-4 mr-2" />
                Upload
              </TabsTrigger>
              <TabsTrigger value="embed">
                <Code className="w-4 h-4 mr-2" />
                Embed
              </TabsTrigger>
            </TabsList>

            <TabsContent value="upload" className="space-y-4">
              <div>
                <Label>Upload Video File</Label>
                <Input
                  type="file"
                  accept="video/*"
                  onChange={handleFileUpload}
                  disabled={uploading}
                />
                {uploading && <p className="text-sm text-muted-foreground mt-2">Uploading...</p>}
              </div>
            </TabsContent>

            <TabsContent value="embed" className="space-y-4">
              <div>
                <Label>YouTube/Vimeo URL or Embed Code</Label>
                <Textarea
                  placeholder="https://www.youtube.com/watch?v=... or <iframe>...</iframe>"
                  value={embedCode}
                  onChange={(e) => setEmbedCode(e.target.value)}
                  rows={4}
                />
              </div>
              <Button onClick={handleEmbedCode} className="w-full">
                Embed Video
              </Button>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );
}
